/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useEffect, useState } from "react";
import { AlertColor, Autocomplete, Box, Tooltip } from "@mui/material";
import { useEnvironmentService } from "../services/registry/EnvironmentService";
import { IEnvironment, LOCAL_ENV } from "../types/EnvironmentTypes";
import { Notify } from "./Notify";
import TextField from "@mui/material/TextField";
import { isRunningLocally, sortEnvironmentList } from "../utils/Environment";
import { useSearchParams } from "react-router-dom";

export interface EnvironmentSelectorProps {
  id: string;
  selectedEnvironment: IEnvironment | undefined;
  onEnvironmentSelected: (env: IEnvironment | undefined) => void;
  errorMessage?: string;
  disabled?: boolean;
  useStorage?: boolean;
  minStage?: number;
  maxStage?: number;
}

export const EnvironmentSelector = ({
  id,
  selectedEnvironment,
  onEnvironmentSelected,
  errorMessage,
  disabled,
  useStorage,
  minStage,
  maxStage,
}: EnvironmentSelectorProps) => {
  const environmentService = useEnvironmentService();
  const [searchParams, setSearchParams] = useSearchParams();

  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [environments, setEnvironments] = useState<IEnvironment[]>([]);

  useEffect(() => {
    if (!selectedEnvironment) {
      if (useStorage) {
        const storedEnvironmentName = localStorage.getItem("selectedEnvironment");
        if (storedEnvironmentName) {
          const e = environments.find((e) => e.name === storedEnvironmentName);
          onEnvironmentSelected(e);
          return;
        }
      }
      const env = searchParams.get("env");
      if (env) {
        const e = environments.find((e) => e.name == env);
        if (e) {
          onEnvironmentSelected(e);
          return;
        }
      }
      if (isRunningLocally()) {
        onEnvironmentSelected(LOCAL_ENV);
      } else {
        for (const e of environments) {
          if (e.isDefault) {
            onEnvironmentSelected(e);
          }
        }
      }
    }
  }, [environments, selectedEnvironment, onEnvironmentSelected, searchParams, useStorage]);

  useEffect(() => {
    (async () => fetchEnvironments())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEnvironments = async () => {
    try {
      const { list } = await environmentService.listEnvironments();
      if (isRunningLocally()) {
        setEnvironments([LOCAL_ENV].concat(...list));
      } else {
        setEnvironments(list);
      }
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to load available environments.");
      setAlertSeverity("error");
    }
  };

  const environmentChangeHandler = (event: React.SyntheticEvent, value: IEnvironment | null) => {
    onEnvironmentSelected(value || undefined);
    if (value) {
      setSearchParams((t) => {
        t.set("env", value.name);
        return t;
      });
      if (useStorage) {
        localStorage.setItem("selectedEnvironment", value.name);
      }
    }
  };

  return (
    <>
      <Autocomplete
        id={`environment-selector-${id}`}
        options={sortEnvironmentList(environments, minStage, maxStage)}
        getOptionLabel={(option) => option.name}
        value={selectedEnvironment || null}
        onChange={environmentChangeHandler}
        disabled={disabled}
        renderInput={(params) => (
          <Tooltip title={selectedEnvironment?.name || ""} placement="top" arrow>
            <TextField
              {...params}
              label="Environment"
              variant="filled"
              error={errorMessage !== undefined}
              helperText={errorMessage}
              sx={{
                background: "white",
                minWidth: "250px",
                "& .MuiInputBase-input": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          </Tooltip>
        )}
        renderOption={(props, option) => (
          <Tooltip title={option.name} placement="right" arrow key={option.name}>
            <Box
              component="li"
              {...props}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {option.name}
            </Box>
          </Tooltip>
        )}
        isOptionEqualToValue={(option, value) => option.name === value.name}
      />
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
    </>
  );
};
